import React, { useState, useEffect, useCallback, useRef } from "react";
import InstitutionCampusDropdown from "../components/InstitutionCampusDropdown";
import DragDropUpload from "../components/DragDropUpload";
import Step2Address from "./Step2Address";
import axios from "../api/Axios";

// Step1 Component - Move outside to prevent recreation
const Step1 = ({
  step1Data,
  handleStep1Change,
  institution,
  campus,
  setInstitution,
  setCampus,
  validateStep1,
  submitStep1Data,
  loadingStates,
  nextStep,
  validationErrors,
  setValidationErrors,
  showStep1ConfirmModal,
  setShowStep1ConfirmModal,
  setStep1Locked,
  lockedFields,
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  getDocumentCardState,
  handleDocumentClick,
  resetAllUploadingStates,
  documentErrors,
  validateDocuments,
  selectedDocument,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleClearSelection,
  handleUploadStart
}) => {

  // Provide default for lockedFields if not passed
  const locked = lockedFields || {};
  console.log('[Step1] Locked fields:', locked);
  
  const handleNext = async () => {
    console.log('Step1 handleNext called');
    
    // First validate Step 1 form data
    const errors = validateStep1();
    console.log('Step1 validation errors:', errors);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Then validate documents
    console.log('Validating documents...');
    const documentsValid = validateDocuments();
    console.log('Documents valid:', documentsValid);
    
    if (!documentsValid) {
      // Scroll to documents section to show errors
      document.getElementById("Required Documents")?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      return;
    }
    
    setValidationErrors({});
    console.log('All validations passed, submitting directly');
    
    // Submit directly without confirmation modal
    await handleConfirmedSubmit();
  };

  // Handle confirmed submission
  const handleConfirmedSubmit = async () => {
    console.log('handleConfirmedSubmit called');
    setShowStep1ConfirmModal(false);
    const success = await submitStep1Data();
    console.log('Step1 data submission success:', success);
    if (success) {
      setStep1Locked(true);
      console.log('Calling nextStep to go to step 2');
      nextStep();
    }
  };

  // Cleanup function to reset all states on component unmount
  useEffect(() => {
    return () => {
      // Reset all uploading states when component unmounts
      resetAllUploadingStates();
    };
  }, [resetAllUploadingStates]);

  return (
    <div>
      <h3>Identification</h3>

      {/* Citizenship and ID Document Type - Horizontally aligned */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.citizenship ? 'is-invalid' : ''}`}
              id="citizenshipSelect"
              value={step1Data.citizenship}
              onChange={(e) => handleStep1Change('citizenship', e.target.value)}
              disabled={locked.citizenship}
            >
              <option value="">Select Citizenship</option>
              <option value="South Africa">South Africa</option>
              <option value="Afghanistan">Afghanistan</option>
              <option value="Albania">Albania</option>
              <option value="Algeria">Algeria</option>
              <option value="Andorra">Andorra</option>
              <option value="Angola">Angola</option>
              <option value="Antigua and Barbuda">Antigua and Barbuda</option>
              <option value="Argentina">Argentina</option>
              <option value="Armenia">Armenia</option>
              <option value="Australia">Australia</option>
              <option value="Austria">Austria</option>
              <option value="Azerbaijan">Azerbaijan</option>
              <option value="Bahamas">Bahamas</option>
              <option value="Bahrain">Bahrain</option>
              <option value="Bangladesh">Bangladesh</option>
              <option value="Barbados">Barbados</option>
              <option value="Belarus">Belarus</option>
              <option value="Belgium">Belgium</option>
              <option value="Belize">Belize</option>
              <option value="Benin">Benin</option>
              <option value="Bhutan">Bhutan</option>
              <option value="Bolivia">Bolivia</option>
              <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
              <option value="Botswana">Botswana</option>
              <option value="Brazil">Brazil</option>
              <option value="Brunei">Brunei</option>
              <option value="Bulgaria">Bulgaria</option>
              <option value="Burkina Faso">Burkina Faso</option>
              <option value="Burundi">Burundi</option>
              <option value="Cabo Verde">Cabo Verde</option>
              <option value="Cambodia">Cambodia</option>
              <option value="Cameroon">Cameroon</option>
              <option value="Canada">Canada</option>
              <option value="Central African Republic">Central African Republic</option>
              <option value="Chad">Chad</option>
              <option value="Chile">Chile</option>
              <option value="China">China</option>
              <option value="Colombia">Colombia</option>
              <option value="Comoros">Comoros</option>
              <option value="Congo, Democratic Republic of the">Congo, Democratic Republic of the</option>
              <option value="Congo, Republic of the">Congo, Republic of the</option>
              <option value="Costa Rica">Costa Rica</option>
              <option value="Cote d'Ivoire">Cote d'Ivoire</option>
              <option value="Croatia">Croatia</option>
              <option value="Cuba">Cuba</option>
              <option value="Cyprus">Cyprus</option>
              <option value="Czech Republic">Czech Republic</option>
              <option value="Denmark">Denmark</option>
              <option value="Djibouti">Djibouti</option>
              <option value="Dominica">Dominica</option>
              <option value="Dominican Republic">Dominican Republic</option>
              <option value="East Timor">East Timor</option>
              <option value="Ecuador">Ecuador</option>
              <option value="Egypt">Egypt</option>
              <option value="El Salvador">El Salvador</option>
              <option value="Equatorial Guinea">Equatorial Guinea</option>
              <option value="Eritrea">Eritrea</option>
              <option value="Estonia">Estonia</option>
              <option value="Eswatini">Eswatini</option>
              <option value="Ethiopia">Ethiopia</option>
              <option value="Fiji">Fiji</option>
              <option value="Finland">Finland</option>
              <option value="France">France</option>
              <option value="Gabon">Gabon</option>
              <option value="Gambia">Gambia</option>
              <option value="Georgia">Georgia</option>
              <option value="Germany">Germany</option>
              <option value="Ghana">Ghana</option>
              <option value="Greece">Greece</option>
              <option value="Grenada">Grenada</option>
              <option value="Guatemala">Guatemala</option>
              <option value="Guinea">Guinea</option>
              <option value="Guinea-Bissau">Guinea-Bissau</option>
              <option value="Guyana">Guyana</option>
              <option value="Haiti">Haiti</option>
              <option value="Honduras">Honduras</option>
              <option value="Hungary">Hungary</option>
              <option value="Iceland">Iceland</option>
              <option value="India">India</option>
              <option value="Indonesia">Indonesia</option>
              <option value="Iran">Iran</option>
              <option value="Iraq">Iraq</option>
              <option value="Ireland">Ireland</option>
              <option value="Israel">Israel</option>
              <option value="Italy">Italy</option>
              <option value="Jamaica">Jamaica</option>
              <option value="Japan">Japan</option>
              <option value="Jordan">Jordan</option>
              <option value="Kazakhstan">Kazakhstan</option>
              <option value="Kenya">Kenya</option>
              <option value="Kiribati">Kiribati</option>
              <option value="Korea, North">Korea, North</option>
              <option value="Korea, South">Korea, South</option>
              <option value="Kosovo">Kosovo</option>
              <option value="Kuwait">Kuwait</option>
              <option value="Kyrgyzstan">Kyrgyzstan</option>
              <option value="Laos">Laos</option>
              <option value="Latvia">Latvia</option>
              <option value="Lebanon">Lebanon</option>
              <option value="Lesotho">Lesotho</option>
              <option value="Liberia">Liberia</option>
              <option value="Libya">Libya</option>
              <option value="Liechtenstein">Liechtenstein</option>
              <option value="Lithuania">Lithuania</option>
              <option value="Luxembourg">Luxembourg</option>
              <option value="Madagascar">Madagascar</option>
              <option value="Malawi">Malawi</option>
              <option value="Malaysia">Malaysia</option>
              <option value="Maldives">Maldives</option>
              <option value="Mali">Mali</option>
              <option value="Malta">Malta</option>
              <option value="Marshall Islands">Marshall Islands</option>
              <option value="Mauritania">Mauritania</option>
              <option value="Mauritius">Mauritius</option>
              <option value="Mexico">Mexico</option>
              <option value="Micronesia">Micronesia</option>
              <option value="Moldova">Moldova</option>
              <option value="Monaco">Monaco</option>
              <option value="Mongolia">Mongolia</option>
              <option value="Montenegro">Montenegro</option>
              <option value="Morocco">Morocco</option>
              <option value="Mozambique">Mozambique</option>
              <option value="Myanmar">Myanmar</option>
              <option value="Namibia">Namibia</option>
              <option value="Nauru">Nauru</option>
              <option value="Nepal">Nepal</option>
              <option value="Netherlands">Netherlands</option>
              <option value="New Zealand">New Zealand</option>
              <option value="Nicaragua">Nicaragua</option>
              <option value="Niger">Niger</option>
              <option value="Nigeria">Nigeria</option>
              <option value="North Macedonia">North Macedonia</option>
              <option value="Norway">Norway</option>
              <option value="Oman">Oman</option>
              <option value="Pakistan">Pakistan</option>
              <option value="Palau">Palau</option>
              <option value="Palestine">Palestine</option>
              <option value="Panama">Panama</option>
              <option value="Papua New Guinea">Papua New Guinea</option>
              <option value="Paraguay">Paraguay</option>
              <option value="Peru">Peru</option>
              <option value="Philippines">Philippines</option>
              <option value="Poland">Poland</option>
              <option value="Portugal">Portugal</option>
              <option value="Qatar">Qatar</option>
              <option value="Romania">Romania</option>
              <option value="Russia">Russia</option>
              <option value="Rwanda">Rwanda</option>
              <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
              <option value="Saint Lucia">Saint Lucia</option>
              <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
              <option value="Samoa">Samoa</option>
              <option value="San Marino">San Marino</option>
              <option value="Sao Tome and Principe">Sao Tome and Principe</option>
              <option value="Saudi Arabia">Saudi Arabia</option>
              <option value="Senegal">Senegal</option>
              <option value="Serbia">Serbia</option>
              <option value="Seychelles">Seychelles</option>
              <option value="Sierra Leone">Sierra Leone</option>
              <option value="Singapore">Singapore</option>
              <option value="Slovakia">Slovakia</option>
              <option value="Slovenia">Slovenia</option>
              <option value="Solomon Islands">Solomon Islands</option>
              <option value="Somalia">Somalia</option>
              <option value="South Africa">South Africa</option>
              <option value="South Sudan">South Sudan</option>
              <option value="Spain">Spain</option>
              <option value="Sri Lanka">Sri Lanka</option>
              <option value="Sudan">Sudan</option>
              <option value="Suriname">Suriname</option>
              <option value="Sweden">Sweden</option>
              <option value="Switzerland">Switzerland</option>
              <option value="Syria">Syria</option>
              <option value="Taiwan">Taiwan</option>
              <option value="Tajikistan">Tajikistan</option>
              <option value="Tanzania">Tanzania</option>
              <option value="Thailand">Thailand</option>
              <option value="Togo">Togo</option>
              <option value="Tonga">Tonga</option>
              <option value="Trinidad and Tobago">Trinidad and Tobago</option>
              <option value="Tunisia">Tunisia</option>
              <option value="Turkey">Turkey</option>
              <option value="Turkmenistan">Turkmenistan</option>
              <option value="Tuvalu">Tuvalu</option>
              <option value="Uganda">Uganda</option>
              <option value="Ukraine">Ukraine</option>
              <option value="United Arab Emirates">United Arab Emirates</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="United States">United States</option>
              <option value="Uruguay">Uruguay</option>
              <option value="Uzbekistan">Uzbekistan</option>
              <option value="Vanuatu">Vanuatu</option>
              <option value="Vatican City">Vatican City</option>
              <option value="Venezuela">Venezuela</option>
              <option value="Vietnam">Vietnam</option>
              <option value="Yemen">Yemen</option>
              <option value="Zambia">Zambia</option>
              <option value="Zimbabwe">Zimbabwe</option>
            </select>
            {validationErrors.citizenship && (
              <div className="invalid-feedback">{validationErrors.citizenship}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.id_document_type ? 'is-invalid' : ''}`}
              id="idDocumentTypeSelect"
              value={step1Data.id_document_type}
              onChange={(e) => handleStep1Change('id_document_type', e.target.value)}
            >
              <option value="">Select ID Document Type</option>
              <option value="passport">Passport</option>
              <option value="national_id">National ID</option>
              <option value="drivers_license">Driver's License</option>
              <option value="refugee travel document">Refugee Travel Document</option>
            </select>
            {validationErrors.id_document_type && (
              <div className="invalid-feedback">{validationErrors.id_document_type}</div>
            )}
          </div>
        </div>
      </div>

      {/* ID Number - Full width */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.id_number ? 'is-invalid' : ''}`}
          type="text"
          id="idNumberInput"
          value={step1Data.id_number}
          onChange={(e) => handleStep1Change('id_number', e.target.value)}
          placeholder="ID Number"
          disabled={locked.id_number}
        />
        <label htmlFor="idNumberInput">ID Number</label>
        {validationErrors.id_number && (
          <div className="invalid-feedback">{validationErrors.id_number}</div>
        )}
      </div>



      {/* Phone Number - Full width at bottom */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.phone_number ? 'is-invalid' : ''}`}
          type="tel"
          id="phoneNumberInput"
          value={step1Data.phone_number}
          onChange={(e) => handleStep1Change('phone_number', e.target.value)}
          placeholder="Phone Number"
          disabled={locked.phone_number}
        />
        <label htmlFor="phoneNumberInput">Phone Number</label>
        {validationErrors.phone_number && (
          <div className="invalid-feedback">{validationErrors.phone_number}</div>
        )}
      </div>

      <h3>Required Document</h3>
      <div id="Required Documents" className="row g-3 mb-3">
        {/* ID/Passport Document - Always shown */}
        <DragDropUpload
          documentType="id_passport"
          title="Photo of Identity/Passport Document"
          onDocumentUploaded={handleDocumentUploaded}
          onDocumentUploadFailed={handleDocumentUploadFailed}
          uploadedFilenames={uploadedFilenames}
          isUploading={uploadingDocuments.has('id_passport')}
          onUploadStart={handleUploadStart}
          hasError={documentErrors.has('id_passport')}
        />
      </div>

      <button
        className="btn btn-primary mt-4"
        onClick={handleNext}
        disabled={loadingStates.step1}
      >
        {loadingStates.step1 ? 'Submitting...' : 'Submit & Continue'}
      </button>

      <style>
        {`
          h3 {
            font-size: 22px;
            color: #495057;
            font-weight: 600;
            margin: 2rem 0 1.5rem 0;
            padding: 0.75rem 0;
            border-bottom: 2px solid #e9ecef;
            position: relative;
          }
          
          h3::before {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 60px;
            height: 2px;
            background: linear-gradient(45deg, #0d6efd, #6610f2);
            border-radius: 1px;
          }
          
          h3:first-child {
            margin-top: 0;
          }
          .doc-card {
            transition: box-shadow 0.2s, transform 0.3s ease;
            animation: fadeInSlide 0.4s ease-in-out;
          }
          .doc-card:hover {
            box-shadow: 0 0 0.5rem #0d6efd33;
            background: #f8f9fa;
            transform: translateY(-2px);
          }
          
          .doc-card.clickable {
            transition: all 0.3s ease;
            user-select: none;
          }
          
          .doc-card.clickable:hover {
            box-shadow: 0 0 1rem #0d6efd44;
            background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
            transform: translateY(-3px);
            border-color: #0d6efd;
          }
          
          .doc-card.clickable:active {
            transform: translateY(-1px);
            box-shadow: 0 0 0.5rem #0d6efd33;
          }
          
          /* Success, error, and uploading states for document cards */
          .bg-success-subtle {
            background-color: rgba(25, 135, 84, 0.1) !important;
          }
          
          .border-success {
            border-color: #198754 !important;
          }
          
          .bg-danger-subtle {
            background-color: rgba(220, 53, 69, 0.1) !important;
          }
          
          .border-danger {
            border-color: #dc3545 !important;
          }
          
          .bg-info-subtle {
            background-color: rgba(13, 110, 253, 0.1) !important;
          }
          
          .border-info {
            border-color: #0d6efd !important;
          }
          
          /* Status indicator positioning */
          .position-absolute {
            position: absolute !important;
          }
          
          .top-0 {
            top: 0 !important;
          }
          
          .end-0 {
            right: 0 !important;
          }
          
          .p-2 {
            padding: 0.5rem !important;
          }
          
          .upload-prompt-container {
            animation: slideDown 0.3s ease-out;
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .upload-prompt {
            position: relative;
            overflow: hidden;
          }
          
          .upload-prompt::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
            transition: left 0.5s ease;
          }
          
          .upload-prompt:hover::before {
            left: 100%;
          }
          
          @keyframes fadeInSlide {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .step-indicator .circle {
            transition: box-shadow 0.2s, border 0.2s, background 0.2s, color 0.2s;
          }
          .step-indicator .circle-active {
            animation: pulse 0.7s;
          }
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 #0d6efd55; }
            70% { box-shadow: 0 0 0 10px #0d6efd22; }
            100% { box-shadow: 0 0 0 0 #0d6efd55; }
          }
          
          /* Locked step styling */
          .step.locked {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }
          
          .step.locked .circle {
            background: #6c757d !important;
            border-color: #6c757d !important;
            color: #fff !important;
          }
          
          .step.locked .label {
            color: #6c757d !important;
          }
          
          /* Document card animations */
          .doc-card {
            transition: box-shadow 0.2s, transform 0.3s ease;
            animation: fadeInSlide 0.4s ease-in-out;
          }
          .doc-card:hover {
            box-shadow: 0 0 0.5rem #0d6efd33;
            background: #f8f9fa;
            transform: translateY(-2px);
          }
          
          .doc-card.clickable {
            transition: all 0.3s ease;
            user-select: none;
          }
          
          /* MDB5 validation styles */
          .form-control.is-invalid,
          .form-select.is-invalid {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
            background-color: rgba(220, 53, 69, 0.05);
          }
          
          .form-control.is-invalid:focus,
          .form-select.is-invalid:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
            background-color: rgba(220, 53, 69, 0.05);
          }
          
          .form-check-input.is-invalid {
            border-color: #dc3545;
          }
          
          .form-check-input.is-invalid:focus {
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
          
          .invalid-feedback {
            display: block;
            width: 100%;
            margin-top: 0.5rem;
            font-size: 0.875rem;
            color: #fff;
            text-align: left;
            position: relative;
            z-index: 1;
            padding: 0.5rem 0.75rem;
            background: #dc3545;
            border-radius: 0.375rem;
            border: 1px solid #dc3545;
          }
          
          /* For form containers, ensure validation message doesn't overlap */
          .form-outline .invalid-feedback,
          .form-floating .invalid-feedback {
            margin-top: 0.5rem;
            position: relative;
            clear: both;
          }
          
          /* Add arrow pointer to validation message */
          .invalid-feedback::before {
            content: '';
            position: absolute;
            top: -5px;
            left: 1rem;
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 5px solid #dc3545;
          }
          
          .form-check.is-invalid .form-check-label {
            color: #dc3545;
          }
          
          /* Remove default select styling */
          select.form-select {
            appearance: none;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
            background-position: right 12px center;
            background-repeat: no-repeat;
            background-size: 16px 12px;
            padding-right: 40px;
          }
          
          /* Month input styling for better clickability */
          input[type="month"].form-control {
            cursor: pointer;
            position: relative;
          }
          
          input[type="month"].form-control::-webkit-calendar-picker-indicator {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: auto;
            height: auto;
            color: transparent;
            background: transparent;
            cursor: pointer;
          }
          
          /* Ensure month input shows calendar icon */
          input[type="month"].form-control::-webkit-inner-spin-button,
          input[type="month"].form-control::-webkit-calendar-picker-indicator {
            opacity: 1;
          }
          
          /* Phone Input Styling to match MDB5 */
          .PhoneInput {
            position: relative;
            display: flex;
            align-items: center;
            border: 1px solid #ced4da;
            border-radius: 4px;
            background: transparent;
            padding: 0;
            font-size: 16px;
            line-height: 1.5;
            width: 100%;
            min-height: 58px;
            height: 58px;
          }
          
          .PhoneInput:focus-within {
            border-color: #0d6efd;
            box-shadow: none;
          }
          
          .PhoneInput.phone-input-invalid {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
          
          .PhoneInput.phone-input-invalid:focus-within {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
          
          .PhoneInputCountrySelect {
            border: none;
            background: transparent;
            margin: 0;
            padding: 16px 12px;
            font-size: 16px;
            outline: none;
            min-width: 80px;
            height: 100%;
          }
          
          .PhoneInputCountrySelect:focus {
            outline: none;
          }
          
          .PhoneInputCountryIcon {
            margin-right: 8px;
            width: 20px;
            height: 15px;
          }
          
          .PhoneInputInput {
            border: none;
            background: transparent;
            padding: 16px 12px 16px 8px;
            font-size: 16px;
            line-height: 1.5;
            width: 100%;
            outline: none;
            flex: 1;
            height: 100%;
          }
          
          .PhoneInputInput::placeholder {
            color: #6c757d;
          }
          
          .PhoneInputInput:focus {
            outline: none;
          }
          
          /* Adjust the label positioning for phone input */
          .form-outline:has(.PhoneInput) .form-label {
            top: -8px;
            font-size: 12px;
            color: #6c757d;
          }
          
          .form-outline:has(.PhoneInput:focus-within) .form-label {
            color: #0d6efd;
            font-size: 12px;
            top: -8px;
          }
        `}
      </style>
    </div>
  );
};

const Step3Academic = ({ 
  previousStep, 
  nextStep, 
  onMount,
  // Step 1 data and handlers for Institution and Enrollment
  step1Data,
  handleStep1Change,
  institution,
  campus,
  setInstitution,
  setCampus,
  validationErrors,
  setValidationErrors,
  validateAcademicRecord,
  lockedFields = {},
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  documentErrors,
  setDocumentErrors,
  getDocumentCardState,
  handleDocumentClick,
  resetAllUploadingStates,
  selectedDocument,
  setSelectedDocument,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleClearSelection,
  handleUploadStart
}) => {
  const locked = lockedFields || {};
  console.log('[Step3Academic] Locked fields:', locked);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [academicRecords, setAcademicRecords] = useState([]);
  const [fetchingRecords, setFetchingRecords] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // New state for initial load
  const hasLoadedRecords = useRef(false); // Track if we've already loaded records

  // Academic record form state
  const [recordData, setRecordData] = useState({
    institution: "",
    campus: "",
    course: "",
    start_date: "",
    end_date: "",
    year_of_study: "",
    study_level: ""
  });

  // Get user id from localStorage
  const userId = localStorage.getItem("user_id");

  // Fetch records when component mounts
  useEffect(() => {
    // Only load records if we haven't already loaded them
    if (hasLoadedRecords.current) return;
    
    const currentUserId = localStorage.getItem("user_id");
    if (onMount) onMount(); // Reset the flag in parent component
    
    // Load records immediately on mount - no unnecessary delays
    const loadRecords = async () => {
      if (!currentUserId) return;
      setFetchingRecords(true);
      try {
        const response = await axios.get(`/accounts/academic-records/user/${currentUserId}/`);
        setAcademicRecords(response.data);
        hasLoadedRecords.current = true; // Mark as loaded
      } catch (err) {
        console.error("Failed to fetch academic records:", err);
      } finally {
        setFetchingRecords(false);
        setInitialLoading(false);
      }
    };
    
    loadRecords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const handleRecordChange = (field, value) => {
    setRecordData(prev => ({ ...prev, [field]: value }));
  };

  const validateRecordForm = () => {
    const errors = {};
    
    if (!recordData.institution) {
      errors.institution = "Institution is required";
    }
    
    if (!recordData.campus) {
      errors.campus = "Campus is required";
    }
    
    if (!recordData.course) {
      errors.course = "Course is required";
    }
    
    if (!recordData.start_date) {
      errors.start_date = "Start date is required";
    }
    
    if (!recordData.year_of_study) {
      errors.year_of_study = "Year of study is required";
    } else {
      // Validate year of study choices
      const validYears = ['1', '2', '3', '4', '5'];
      if (!validYears.includes(recordData.year_of_study)) {
        errors.year_of_study = `Year of study must be one of: ${validYears.join(', ')}`;
      }
    }
    
    if (!recordData.study_level) {
      errors.study_level = "Study level is required";
    } else {
      // Validate study level choices
      const validLevels = ['Undergraduate', 'Honors', 'Masters', 'PhD'];
      if (!validLevels.includes(recordData.study_level)) {
        errors.study_level = `Study level must be one of: ${validLevels.join(', ')}`;
      }
    }
    
    // Validate that end date is after start date (only if end date is provided)
    if (recordData.start_date && recordData.end_date && recordData.start_date >= recordData.end_date) {
      errors.end_date = "End date must be after start date";
    }
    
    return errors;
  };

  const handleRecordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    // Validate form
    const validationErrors = validateRecordForm();
    if (Object.keys(validationErrors).length > 0) {
      setError(Object.values(validationErrors)[0]); // Show first error
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        user: Number(userId),
        institution: Number(recordData.institution),
        campus: Number(recordData.campus),
        course: recordData.course,
        start_date: recordData.start_date,
        end_date: recordData.end_date,
        year_of_study: recordData.year_of_study,
        study_level: recordData.study_level
      };
      await axios.post("/accounts/academic-records/create/", payload);
      setSuccess("Academic record added!");
      // Refresh the records list
      try {
        const response = await axios.get(`/accounts/academic-records/user/${userId}/`);
        setAcademicRecords(response.data);
      } catch (err) {
        console.error("Failed to refresh academic records:", err);
      }
      // Reset form
      setRecordData({
        institution: "",
        campus: "",
        course: "",
        start_date: "",
        end_date: "",
        year_of_study: "",
        study_level: ""
      });
      // Close the lightbox after a short delay to show success message
      setTimeout(() => {
        setShowForm(false);
        setSuccess("");
      }, 1500);
    } catch (err) {
      console.error("Academic record creation error:", err.response ? err.response.data : err);
      
      // Parse and display validation errors
      if (err.response && err.response.data) {
        const errors = err.response.data;
        let errorMessage = "";
        
        // Format error messages for user display
        Object.keys(errors).forEach(field => {
          const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
          fieldErrors.forEach(error => {
            let friendlyFieldName = field;
            switch(field) {
              case 'user': friendlyFieldName = 'User'; break;
              case 'institution': friendlyFieldName = 'Institution'; break;
              case 'campus': friendlyFieldName = 'Campus'; break;
              case 'start_date': friendlyFieldName = 'Start Date'; break;
              case 'end_date': friendlyFieldName = 'End Date'; break;
              case 'year_of_study': friendlyFieldName = 'Year of Study'; break;
              case 'study_level': friendlyFieldName = 'Study Level'; break;
              default: friendlyFieldName = field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
            }
            errorMessage += `${friendlyFieldName}: ${error}. `;
          });
        });
        
        setError(errorMessage.trim());
      } else {
        setError("Failed to add academic record. Please check your data and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAcademicRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this academic record?')) {
      return;
    }
    
    try {
      await axios.delete(`/accounts/academic-records/delete/${recordId}/`);
      
      // Refresh the records list
      const currentUserId = localStorage.getItem("user_id");
      if (currentUserId) {
        const response = await axios.get(`/accounts/academic-records/user/${currentUserId}/`);
        setAcademicRecords(response.data);
      }
    } catch (err) {
      console.error("Failed to delete academic record:", err);
      setError("Failed to delete academic record. Please try again.");
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setError("");
    setSuccess("");
    // Reset form when closing manually
    setRecordData({
      institution: "",
      campus: "",
      start_date: "",
      end_date: "",
      year_of_study: "",
      study_level: ""
    });
  };

  return (
    <div>
      <h3>Academic Record</h3>
      <div className="alert alert-info d-flex align-items-center mb-4" style={{ fontSize: "1rem" }}>
        <i className="bi bi-info-circle-fill me-2" style={{ fontSize: "1.5rem", color: "#0d6efd" }}></i>
        <div>
          <strong>Why is this required?</strong><br />
          This information forms part of your profile and can be viewed by accommodation providers when you request a booking.
          Most hosts conduct background checks privately and full disclosure helps avoid booking delays or rejections.
        </div>
      </div>

      <h3>Institution and Enrollment</h3>

      {/* Enrollment Status Radio Buttons */}
      <div className="mb-3">
        <label className="form-label"><strong>Enrollment Status</strong></label>
        <div>
          <label className="me-3">
            <input
              type="radio"
              name="enrollment_status"
              value="currently_registered"
              checked={step1Data.enrollment_status === "currently_registered" || !step1Data.enrollment_status}
              onChange={() => handleStep1Change("enrollment_status", "currently_registered")}
            />{" "}
            Currently registered at the institution
          </label>
          <label className="me-3">
            <input
              type="radio"
              name="enrollment_status"
              value="accepted"
              checked={step1Data.enrollment_status === "accepted"}
              onChange={() => handleStep1Change("enrollment_status", "accepted")}
            />{" "}
            Accepted for study at chosen institution
          </label>
          <label>
            <input
              type="radio"
              name="enrollment_status"
              value="prospective"
              checked={step1Data.enrollment_status === "prospective"}
              onChange={() => handleStep1Change("enrollment_status", "prospective")}
            />{" "}
            Prospective student
          </label>
        </div>
      </div>

      <InstitutionCampusDropdown
        institutionValue={institution}
        campusValue={campus}
        onInstitutionChange={(value) => {
          setInstitution(value);
          // Clear validation error when user selects institution
          if (validationErrors.institution) {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.institution;
              return newErrors;
            });
          }
        }}
        onCampusChange={(value) => {
          setCampus(value);
          // Clear validation error when user selects campus
          if (validationErrors.campus) {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.campus;
              return newErrors;
            });
          }
        }}
        validationErrors={validationErrors}
        institutionDisabled={locked.institution}
        campusDisabled={locked.campus}
      />

      {/* Student No, Course, Year of Study */}
      {step1Data.enrollment_status !== "prospective" && (
        <>
          {step1Data.enrollment_status === "currently_registered" && (
            <>
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.student_number ? 'is-invalid' : ''}`}
                  type="text"
                  id="studentNumberInput"
                  value={step1Data.student_number || ""}
                  onChange={e => handleStep1Change("student_number", e.target.value)}
                  placeholder="Student No"
                  disabled={locked.student_number}
                />
                <label htmlFor="studentNumberInput">Student No</label>
                {validationErrors.student_number && (
                  <div className="invalid-feedback">{validationErrors.student_number}</div>
                )}
              </div>

              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.course ? 'is-invalid' : ''}`}
                  type="text"
                  id="courseInput"
                  value={step1Data.course || ""}
                  onChange={e => handleStep1Change("course", e.target.value)}
                  placeholder="Course"
                  disabled={locked.course}
                />
                <label htmlFor="courseInput">Course</label>
                {validationErrors.course && (
                  <div className="invalid-feedback">{validationErrors.course}</div>
                )}
              </div>

              <div className="form-floating mb-3">
                <select
                  className={`form-select ${validationErrors.year_of_study ? 'is-invalid' : ''}`}
                  id="yearOfStudyInput"
                  value={step1Data.year_of_study || ""}
                  onChange={e => handleStep1Change("year_of_study", e.target.value)}
                >
                  <option value="">Year of Study</option>
                  <option value="1">First Year</option>
                  <option value="2">Second Year</option>
                  <option value="3">Third Year</option>
                  <option value="4">Fourth Year</option>
                  <option value="5">Fifth Year</option>
                </select>
                {validationErrors.year_of_study && (
                  <div className="invalid-feedback">{validationErrors.year_of_study}</div>
                )}
              </div>
            </>
          )}
          {step1Data.enrollment_status === "accepted" && (
            <>
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.course ? 'is-invalid' : ''}`}
                  type="text"
                  id="courseAcceptedInput"
                  value={step1Data.course || ""}
                  onChange={e => handleStep1Change("course", e.target.value)}
                  placeholder="Course"
                  disabled={locked.course}
                />
                <label htmlFor="courseAcceptedInput">Course</label>
                {validationErrors.course && (
                  <div className="invalid-feedback">{validationErrors.course}</div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Year of Registration and Study Level - Hide for prospective students */}
      {step1Data.enrollment_status !== "prospective" && (
        <>
      {/* Year of Registration and Study Level - Horizontally aligned */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.year_of_registration ? 'is-invalid' : ''}`}
              type="month"
              id="yearOfRegistrationInput"
              value={step1Data.year_of_registration || ""}
              onChange={e => handleStep1Change("year_of_registration", e.target.value)}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              max={new Date().toISOString().slice(0, 7)}
              placeholder="YYYY-MM"
            />
            <label htmlFor="yearOfRegistrationInput">Registration Date</label>
            <small className="form-text text-muted">Enter the month and year you registered (e.g., 2023-09)</small>
            {validationErrors.year_of_registration && (
              <div className="invalid-feedback">{validationErrors.year_of_registration}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.study_level ? 'is-invalid' : ''}`}
              id="studyLevelInput"
              value={step1Data.study_level || ""}
              onChange={e => handleStep1Change("study_level", e.target.value)}
            >
              <option value="">Study Level</option>
              <option value="Undergraduate">Undergraduate</option>
              <option value="Honors">Honors</option>
              <option value="Masters">Masters</option>
              <option value="PhD">PhD</option>
            </select>
            {validationErrors.study_level && (
              <div className="invalid-feedback">{validationErrors.study_level}</div>
            )}
          </div>
        </div>
      </div>
        </>
      )}
      
      
      {/* Show initial loading screen */}
      {initialLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3">
            <h5>Loading your academic records...</h5>
            <p className="text-muted">Please wait while we load your academic records</p>
          </div>
        </div>
      ) : (
        <>
      {showForm && (
        <div
          className="lightbox-backdrop"
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            className="lightbox-content bg-white p-4 rounded shadow"
            style={{ minWidth: 350, maxWidth: 500, position: "relative" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Enhanced close button - prominent red X at extreme top */}
            <button
              type="button"
              onClick={handleCloseForm}
              style={{ 
                position: "absolute", 
                top: -10, 
                right: -10, 
                width: "30px", 
                height: "30px",
                borderRadius: "50%",
                border: "2px solid #dc3545",
                backgroundColor: "#dc3545",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                zIndex: 1001,
                boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
              }}
              aria-label="Close"
            >
              ✕
            </button>
            <form onSubmit={handleRecordSubmit}>
          
              <InstitutionCampusDropdown
                institutionValue={recordData.institution}
                campusValue={recordData.campus}
                onInstitutionChange={value => handleRecordChange("institution", value)}
                onCampusChange={value => handleRecordChange("campus", value)}
                institutionLabel="Institution"
                campusLabel="Campus"
              />

              <div className="form-floating mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="courseModalInput"
                  value={recordData.course}
                  onChange={e => handleRecordChange("course", e.target.value)}
                  required
                  placeholder="Course"
                />
                <label htmlFor="courseModalInput">Course</label>
              </div>
           
              <div className="form-floating mb-3">
                <input
                  type="date"
                  className="form-control"
                  id="startDateInput"
                  value={recordData.start_date}
                  onChange={e => handleRecordChange("start_date", e.target.value)}
                  required
                  placeholder="Start Date"
                />
                <label htmlFor="startDateInput">Start Date</label>
              </div>

              <div className="form-floating mb-3">
                <input
                  type="date"
                  className="form-control"
                  id="endDateInput"
                  value={recordData.end_date}
                  onChange={e => handleRecordChange("end_date", e.target.value)}
                  required
                  placeholder="End Date"
                />
                <label htmlFor="endDateInput">End Date</label>
              </div>

              <div className="form-floating mb-3">
                <select
                  className="form-select"
                  id="yearOfStudyModalInput"
                  value={recordData.year_of_study}
                  onChange={e => handleRecordChange("year_of_study", e.target.value)}
                  required
                >
                  <option value="">Year of Study</option>
                  <option value="1">1st Year</option>
                  <option value="2">2nd Year</option>
                  <option value="3">3rd Year</option>
                  <option value="4">4th Year</option>
                  <option value="5">5th Year</option>
                </select>
              </div>

              <div className="form-floating mb-3">
                <select
                  className="form-select"
                  id="studyLevelModalInput"
                  value={recordData.study_level}
                  onChange={e => handleRecordChange("study_level", e.target.value)}
                  required
                >
                  <option value="">Study Level</option>
                  <option value="Undergraduate">Undergraduate</option>
                  <option value="Honors">Honors</option>
                  <option value="Masters">Masters</option>
                  <option value="PhD">PhD</option>
                </select>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Academic Records Table */}
      <div className="mt-4">
        <h5>Your Academic Records</h5>
        {fetchingRecords ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : academicRecords.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Institution</th>
                  <th>Campus</th>
                  <th>Course</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Year of Study</th>
                  <th>Study Level</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {academicRecords.map((record) => {
                  const getYearDisplay = (year) => {
                    const yearMap = {
                      '1': 'First',
                      '2': 'Second',
                      '3': 'Third',
                      '4': 'Fourth',
                      '5': 'Fifth'
                    };
                    return yearMap[year] || year;
                  };

                  const getStudyLevelDisplay = (level) => {
                    const levelMap = {
                      'Undergraduate': 'Undergraduate',
                      'Honors': 'Honors',
                      'Masters': 'Masters',
                      'PhD': 'PhD'
                    };
                    return levelMap[level] || level;
                  };

                  return (
                    <tr key={record.id}>
                      <td>{record.institution_name}</td>
                      <td>{record.campus_name}</td>
                      <td>{record.course || 'N/A'}</td>
                      <td>{new Date(record.start_date).toLocaleDateString()}</td>
                      <td>{record.end_date ? new Date(record.end_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{getYearDisplay(record.year_of_study)}</td>
                      <td>{getStudyLevelDisplay(record.study_level)}</td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDeleteAcademicRecord(record.id)}
                          title="Delete this academic record"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="alert alert-light text-center">
            <i className="bi bi-info-circle me-2"></i>
            No academic records found. Click "Add a Record" to add your first academic record.
          </div>
        )}
      </div>
      
      {/* Add Academic Record Button - Positioned to the right */}
      <div className="d-flex justify-content-end mt-3 mb-4">
        <button
          className="btn btn-outline-primary"
          type="button"
          onClick={() => {
            setError("");
            setSuccess("");
            setShowForm(true);
          }}
        >
          Add a Record
        </button>
      </div>
      
      {/* Required Document Upload Section - Show regardless of academic records */}
      <div className="mt-4">
        <h5>Required Document</h5>
        
        {/* Show different documents based on enrollment status */}
        {step1Data.enrollment_status === "currently_registered" && (
          <div className="row">
            <DragDropUpload
              documentType="student_card"
              title="Student Card"
              onDocumentUploaded={handleDocumentUploaded}
              onDocumentUploadFailed={handleDocumentUploadFailed}
              uploadedFilenames={uploadedFilenames}
              isUploading={uploadingDocuments.has('student_card')}
              onUploadStart={handleUploadStart}
              hasError={documentErrors.has('student_card')}
            />
          </div>
        )}

        {/* Show acceptance letter for accepted students */}
        {step1Data.enrollment_status === "accepted" && (
          <div className="row">
            <DragDropUpload
              documentType="acceptance_letter"
              title="Acceptance Letter"
              onDocumentUploaded={handleDocumentUploaded}
              onDocumentUploadFailed={handleDocumentUploadFailed}
              uploadedFilenames={uploadedFilenames}
              isUploading={uploadingDocuments.has('acceptance_letter')}
              onUploadStart={handleUploadStart}
              hasError={documentErrors.has('acceptance_letter')}
            />
          </div>
        )}

        {/* Show no document required message for prospective students */}
        {step1Data.enrollment_status === "prospective" && (
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            <strong>No document required:</strong> As a prospective student, no additional documentation is needed at this stage.
          </div>
        )}
      </div>
      
      <div className="d-flex justify-content-between mt-4">
        <button className="btn btn-secondary" onClick={previousStep}>Previous</button>
        <button
          className="btn btn-primary"
          onClick={() => {
            // Clear any selected document before proceeding
            if (selectedDocument) {
              setSelectedDocument('');
            }
            
            // First validate form fields
            const formErrors = validateAcademicRecord();
            if (Object.keys(formErrors).length > 0) {
              setValidationErrors(formErrors);
              // Scroll to the first error field
              const firstErrorField = Object.keys(formErrors)[0];
              const errorElement = document.querySelector(`[id*="${firstErrorField}"], .is-invalid`);
              if (errorElement) {
                errorElement.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
              }
              return;
            }
            
            // Clear form validation errors if validation passed
            setValidationErrors({});
            
            // Validate required documents for students with academic records
            if (academicRecords.length > 0) {
              console.log('Step3 validation - enrollment_status:', step1Data.enrollment_status);
              let requiredDocument = null;
              
              // Determine required document based on enrollment status
              if (step1Data.enrollment_status === "currently_registered") {
                requiredDocument = 'student_card';
              } else if (step1Data.enrollment_status === "accepted") {
                requiredDocument = 'acceptance_letter';
              }
              // For prospective students, no document is required
              
              console.log('Required document for Step3:', requiredDocument);
              console.log('Uploaded documents:', uploadedDocuments);
              
              // Check if required document is uploaded (if any)
              if (requiredDocument && !uploadedDocuments.has(requiredDocument)) {
                console.log('Required document missing, setting error');
                setDocumentErrors(prev => new Set([...prev, requiredDocument]));
                // Scroll to document section
                document.querySelector('.mt-4 h5')?.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
                return;
              }
              
              console.log('All required documents validated, proceeding to next step');
            }
            
            // Clear any document errors and proceed
            setDocumentErrors(new Set());
            nextStep();
          }}
        >
          Continue
        </button>
      </div>
      </>
      )}
    </div>
  );
};

const Step4SubstancePolicy = ({ 
  step4Data, 
  handleStep4Change, 
  validateStep4, 
  submitStep4Data, 
  loadingStates, 
  nextStep, 
  previousStep,
  validationErrors,
  setValidationErrors
}) => {
  const handleNext = async () => {
    console.log('Step4 validation - policyAgreed:', step4Data.policyAgreed);
    const errors = validateStep4();
    console.log('Step4 validation errors:', errors);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    const success = await submitStep4Data();
    if (success) nextStep();
  };

  return (
    <div>
      <h3>Substance Use Policy</h3>
      <h5 className="mt-4 mb-2 text-primary">Student Conduct and Drug Policy</h5>
      <p className="mb-3 text-muted">
        <strong>Purpose:</strong> <br />
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam euismod, urna eu tincidunt consectetur, nisi nisl aliquam nunc, nec dictum enim urna a justo.
      </p>
      <h6 className="mt-3">1. General Expectations</h6>
      <ul>
        <li>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</li>
        <li>Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae.</li>
        <li>Morbi facilisis, sapien nec pulvinar dictum, urna urna cursus erat, nec dictum enim urna a justo.</li>
      </ul>
      <h6 className="mt-3">2. Prohibited Substances</h6>
      <ul>
        <li>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</li>
        <li>Aliquam erat volutpat. Nullam ac urna eu felis dapibus condimentum.</li>
        <li>Curabitur euismod, urna eu tincidunt consectetur, nisi nisl aliquam nunc.</li>
      </ul>
      <h6 className="mt-3">3. Disciplinary Actions</h6>
      <ol>
        <li>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</li>
        <li>Phasellus euismod, urna eu tincidunt consectetur, nisi nisl aliquam nunc.</li>
        <li>Morbi facilisis, sapien nec pulvinar dictum, urna urna cursus erat.</li>
      </ol>

       <h6 className="mt-3">4. Substance Testing</h6>
      <ol>
        <li>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</li>
        <li>Phasellus euismod, urna eu tincidunt consectetur, nisi nisl aliquam nunc.</li>
        <li>Morbi facilisis, sapien nec pulvinar dictum, urna urna cursus erat.</li>
      </ol>
      <h6 className="mt-3">5. Support and Resources</h6>
      <p>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque euismod, urna eu tincidunt consectetur, nisi nisl aliquam nunc, nec dictum enim urna a justo.
      </p>
      <div className={`form-check my-3 ${validationErrors.policyAgreed ? 'is-invalid' : ''}`}>
        <input 
          className={`form-check-input ${validationErrors.policyAgreed ? 'is-invalid' : ''}`}
          type="checkbox" 
          id="policyCheck" 
          checked={step4Data.policyAgreed}
          onChange={(e) => {
            handleStep4Change('policyAgreed', e.target.checked);
            // Clear validation error when user checks the box
            if (e.target.checked && validationErrors.policyAgreed) {
              setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.policyAgreed;
                return newErrors;
              });
            }
          }}
        />
        <label className="form-check-label" htmlFor="policyCheck">
          I agree to the substance use policy *
        </label>
        {validationErrors.policyAgreed && (
          <div className="invalid-feedback d-block">{validationErrors.policyAgreed}</div>
        )}
      </div>
      <div className="d-flex justify-content-between mt-4">
        <button className="btn btn-secondary" onClick={previousStep}>Previous</button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext} 
          disabled={loadingStates.step4}
        >
          {loadingStates.step4 ? 'Submitting...' : 'Submit & Continue'}
        </button>
      </div>
    </div>
  );
};

const Step5 = ({ 
  step5Data, 
  handleStep5Change, 
  validateStep5, 
  submitStep5Data, 
  loadingStates, 
  previousStep,
  validationErrors,
  setValidationErrors
}) => {
  const handleSubmit = async () => {
    const errors = validateStep5();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    await submitStep5Data();
  };

  return (
    <div>
      <h3>Source of Funds</h3>
      
      <div className="form-floating mb-3">
        <select 
          className={`form-select ${validationErrors.sourceOfFunds ? 'is-invalid' : ''}`}
          id="sourceOfFundsSelect"
          value={step5Data.sourceOfFunds} 
          onChange={(e) => handleStep5Change('sourceOfFunds', e.target.value)}
        >
          <option value="">Source of Funds</option>
          <option value="self">Self</option>
          <option value="guardian">Guardian / Sponsor</option>
          <option value="bursary">Bursary</option>
          <option value="scholarship">Scholarship</option>
          <option value="study_loan">Study Loan</option>
        </select>
        {validationErrors.sourceOfFunds && (
          <div className="invalid-feedback">{validationErrors.sourceOfFunds}</div>
        )}
      </div>

      <div className="d-flex justify-content-between mt-4">
        <button className="btn btn-secondary" onClick={previousStep}>Previous</button>
        <button 
          className="btn btn-success" 
          onClick={handleSubmit} 
          disabled={loadingStates.step5}
        >
          {loadingStates.step5 ? 'Submitting...' : 'Submit Application'}
        </button>
      </div>
    </div>
  );
};

const CompletionSuccess = () => {
  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '3rem 1rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <div style={{ marginBottom: '2rem' }}>
        <i className="bi bi-check-circle-fill" style={{ fontSize: '4rem', color: '#198754' }}></i>
      </div>
      <div style={{ 
        backgroundColor: '#d1e7dd',
        border: '1px solid #badbcc',
        borderRadius: '0.375rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 0.5rem 1rem rgba(25, 135, 84, 0.15)'
      }}>
        <h4 style={{ color: '#0f5132', marginBottom: '1rem', fontWeight: 'bold' }}>
          <i className="bi bi-check-circle" style={{ marginRight: '0.5rem' }}></i>
          Application Submitted Successfully!
        </h4>
        <p style={{ color: '#0f5132', fontSize: '1.1rem', margin: '0' }}>
          Your details have been saved. We will review your submitted documents and contact you shortly.
        </p>
      </div>
      <div style={{ color: '#6c757d' }}>
        <p style={{ marginBottom: '0.5rem' }}>Thank you for completing your registration.</p>
        <p style={{ margin: '0' }}>You can now close this window.</p>
      </div>
    </div>
  );
};

const StudentFormWizard = () => {
  const [institution, setInstitution] = useState("");
  const [campus, setCampus] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  const [showStep1ConfirmModal, setShowStep1ConfirmModal] = useState(false);
  const [step1Locked, setStep1Locked] = useState(false);
  const [lockedFields, setLockedFields] = useState({});

  const stepLabels = [
    { label: "Identification", icon: "bi-person-badge" },
    { label: "Address", icon: "bi-house-door" },
    { label: "Academic Record", icon: "bi-journal-bookmark" },
    { label: "Substance Policy", icon: "bi-shield-check" },
    { label: "Source of Funds", icon: "bi-cash-coin" }
  ];

  const [step1Data, setStep1Data] = useState({
    citizenship: '',
    id_document_type: '',
    id_number: '',
    phone_number: '',
    enrollment_status: 'currently_registered',
    student_number: '',
    course: '',
    year_of_study: '',
    year_of_registration: '',
    study_level: ''
  });

  const [step2Data, setStep2Data] = useState({
    country: '',
    address_line_1: '',
    address_line_2: '',
    suburb: '',
    city: '',
    postcode: '',
    province: ''
  });

  const [step4Data, setStep4Data] = useState({ 
    policyAgreed: false 
  });
  
  const [step5Data, setStep5Data] = useState({ 
    sourceOfFunds: '' 
  });
  
  const [loadingStates, setLoadingStates] = useState({ 
    step1: false, 
    step2: false,
    step3: false,
    step4: false, 
    step5: false 
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Document upload state
  const [selectedDocument, setSelectedDocument] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState(new Set());
  const [uploadedFilenames, setUploadedFilenames] = useState(new Map()); // Track filenames for each document type
  const [documentErrors, setDocumentErrors] = useState(new Set());
  const [uploadingDocuments, setUploadingDocuments] = useState(new Set());

  // Fetch student and beneficiary data and pre-fill ALL steps
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        console.log('=== FETCHING ALL STUDENT AND BENEFICIARY DATA ===');
        
        // Helper function to check if a value is valid
        const isValidValue = (value) => {
          return value && value !== 'pending' && value !== '';
        };
        
        const fieldsToLock = {};
        
        // Fetch beneficiary data first
        let beneficiaryData = {};
        try {
          const beneficiaryResponse = await axios.get('/accounts/beneficiary-data/');
          console.log('Beneficiary response:', beneficiaryResponse.data);
          if (beneficiaryResponse.data?.exists) {
            beneficiaryData = beneficiaryResponse.data.data;
            console.log('Beneficiary data extracted:', beneficiaryData);
          }
        } catch (beneficiaryError) {
          console.log('No beneficiary data found or error fetching:', beneficiaryError.message);
        }
        
        // Fetch student data
        const studentResponse = await axios.get('/accounts/user/');
        const studentData = studentResponse.data;
        console.log('Student data received:', studentData);
        
        // ========== STEP 1: IDENTIFICATION ==========
        const newStep1Data = { ...step1Data };
        
        // Citizenship: Student data first, fallback to South Africa if beneficiary has race
        const citizenship = isValidValue(studentData.citizenship) 
          ? studentData.citizenship 
          : (beneficiaryData.race ? 'South Africa' : null);
        if (citizenship) {
          newStep1Data.citizenship = citizenship;
          fieldsToLock.citizenship = true;
          console.log('Setting citizenship:', citizenship, '(locked)');
        }
        
        // ID Document Type: Student data first, fallback to beneficiary
        const idDocType = isValidValue(studentData.id_document_type) 
          ? studentData.id_document_type 
          : beneficiaryData.id_document_type;
        if (idDocType) {
          newStep1Data.id_document_type = idDocType;
          fieldsToLock.id_document_type = true;
          console.log('Setting id_document_type:', idDocType, '(locked)');
        }
        
        // ID Number: Student data first, fallback to beneficiary national_id
        const idNumber = isValidValue(studentData.id_number) 
          ? studentData.id_number 
          : beneficiaryData.national_id;
        if (idNumber) {
          newStep1Data.id_number = idNumber;
          fieldsToLock.id_number = true;
          console.log('Setting id_number:', idNumber, '(locked)');
        }
        
        // Phone Number: Student data first, fallback to beneficiary mobile_number
        const phoneNumber = isValidValue(studentData.phone_number) 
          ? studentData.phone_number 
          : beneficiaryData.mobile_number;
        if (phoneNumber) {
          newStep1Data.phone_number = phoneNumber;
          fieldsToLock.phone_number = true;
          console.log('Setting phone_number:', phoneNumber, '(locked)');
        }
        
        // Enrollment Status: Only from student data
        if (isValidValue(studentData.enrollment_status)) {
          newStep1Data.enrollment_status = studentData.enrollment_status;
          fieldsToLock.enrollment_status = true;
          console.log('Setting enrollment_status:', studentData.enrollment_status, '(locked)');
        }
        
        // Student Number: Student data first, fallback to beneficiary
        const studentNumber = isValidValue(studentData.student_number) 
          ? studentData.student_number 
          : beneficiaryData.student_number;
        if (studentNumber) {
          newStep1Data.student_number = studentNumber;
          fieldsToLock.student_number = true;
          console.log('Setting student_number:', studentNumber, '(locked)');
        }
        
        // Course: Student data first, fallback to beneficiary field_of_study
        const course = isValidValue(studentData.course) 
          ? studentData.course 
          : beneficiaryData.field_of_study;
        if (course) {
          newStep1Data.course = course;
          fieldsToLock.course = true;
          console.log('Setting course:', course, '(locked)');
        }
        
        // Year of Study: Pre-filled but NOT locked (students can change it)
        const yearOfStudy = isValidValue(studentData.year_of_study) 
          ? studentData.year_of_study 
          : beneficiaryData.year_of_study;
        if (yearOfStudy) {
          newStep1Data.year_of_study = yearOfStudy;
          console.log('Setting year_of_study:', yearOfStudy, '(not locked)');
        }
        
        // Year of Registration: Only from student data
        if (isValidValue(studentData.year_of_registration)) {
          newStep1Data.year_of_registration = studentData.year_of_registration;
          fieldsToLock.year_of_registration = true;
          console.log('Setting year_of_registration:', studentData.year_of_registration, '(locked)');
        }
        
        // Study Level: Only from student data
        if (isValidValue(studentData.study_level)) {
          newStep1Data.study_level = studentData.study_level;
          fieldsToLock.study_level = true;
          console.log('Setting study_level:', studentData.study_level, '(locked)');
        }
        
        // Institution: Beneficiary data first, fallback to student data
        const institutionId = beneficiaryData.institution_id || studentData.institution_id;
        if (institutionId) {
          setInstitution(institutionId.toString());
          fieldsToLock.institution = true;
          console.log('Setting institution:', institutionId, '(locked)');
        }
        
        // Campus: Beneficiary data first, fallback to student data
        const campusId = beneficiaryData.campus_id || studentData.campus_id;
        if (campusId) {
          setCampus(campusId.toString());
          fieldsToLock.campus = true;
          console.log('Setting campus:', campusId, '(locked)');
        }
        
        console.log('Final newStep1Data:', newStep1Data);
        setStep1Data(newStep1Data);
        
        // ========== STEP 2: ADDRESS ==========
        console.log('=== PRE-FILLING STEP 2: ADDRESS ===');
        const newStep2Data = { ...step2Data };
        
        if (isValidValue(studentData.country)) {
          newStep2Data.country = studentData.country;
          fieldsToLock.country = true;
          console.log('Setting country:', studentData.country, '(locked)');
        }
        
        if (isValidValue(studentData.addresslines)) {
          newStep2Data.address_line_1 = studentData.addresslines;
          fieldsToLock.address_line_1 = true;
          console.log('Setting address_line_1:', studentData.addresslines, '(locked)');
        }
        
        if (isValidValue(studentData.suburb)) {
          newStep2Data.suburb = studentData.suburb;
          fieldsToLock.suburb = true;
          console.log('Setting suburb:', studentData.suburb, '(locked)');
        }
        
        if (isValidValue(studentData.city)) {
          newStep2Data.city = studentData.city;
          fieldsToLock.city = true;
          console.log('Setting city:', studentData.city, '(locked)');
        }
        
        if (isValidValue(studentData.postcode)) {
          newStep2Data.postcode = studentData.postcode;
          fieldsToLock.postcode = true;
          console.log('Setting postcode:', studentData.postcode, '(locked)');
        }
        
        if (isValidValue(studentData.province)) {
          newStep2Data.province = studentData.province;
          fieldsToLock.province = true;
          console.log('Setting province:', studentData.province, '(locked)');
        }
        
        console.log('Final newStep2Data:', newStep2Data);
        setStep2Data(newStep2Data);
        
        console.log('=== SETTING ALL LOCKED FIELDS ===');
        console.log('All locked fields:', fieldsToLock);
        setLockedFields(fieldsToLock);
        
      } catch (error) {
        console.error('Error fetching student/beneficiary data:', error);
      }
    };
    
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - intentionally empty to only run on initial load

  // Get required documents based on enrollment status
  const getRequiredDocuments = () => {
    console.log('getRequiredDocuments called, enrollment_status:', step1Data.enrollment_status);
    // Only require documents that are actually present in Step1
    const required = ['id_passport'];
    
    if (step1Data.enrollment_status === "currently_registered") {
      // Student card will be required in Step3 (Academic Record), not Step1
      // required.push('student_card');
    } else if (step1Data.enrollment_status === "accepted") {
      // Acceptance letter will be required in Step3 (Academic Record), not Step1
      // required.push('acceptance_letter');
    }
    
    console.log('Required documents for Step1:', required);
    return required;
  };

  // Reset all uploading states (clears any stuck spinners)
  const resetAllUploadingStates = useCallback(() => {
    setUploadingDocuments(new Set());
  }, []);

  // Handle successful document upload
  const handleDocumentUploaded = useCallback((docType, filename) => {
    setUploadingDocuments(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setUploadedDocuments(prev => new Set([...(prev || []), docType]));
    
    // Add filename to the list for this document type
    setUploadedFilenames(prev => {
      const newMap = new Map(prev);
      const existingFiles = newMap.get(docType) || [];
      // Add new filename to the list (allow multiple files)
      const updatedFiles = [...existingFiles, filename];
      newMap.set(docType, updatedFiles);
      return newMap;
    });
    
    setDocumentErrors(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setSelectedDocument('');
  }, []);

  // Handle failed document upload
  const handleDocumentUploadFailed = useCallback((docType) => {
    setUploadingDocuments(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setDocumentErrors(prev => new Set([...(prev || []), docType]));
    setSelectedDocument('');
  }, []);

  // Handle upload start
  const handleUploadStart = useCallback((docType) => {
    setUploadingDocuments(prev => new Set([...(prev || []), docType]));
  }, []);

  // Validate documents
  const validateDocuments = () => {
    try {
      const required = getRequiredDocuments();
      const missing = required.filter(doc => !uploadedDocuments.has(doc));
      setDocumentErrors(new Set(missing));
      return missing.length === 0;
    } catch (error) {
      console.error('Error in validateDocuments:', error);
      return false;
    }
  };

  // Get document card state
  const getDocumentCardState = (docType) => {
    if (uploadedDocuments.has(docType)) {
      return 'uploaded'; // Show tick
    } else if (uploadingDocuments.has(docType)) {
      return 'uploading'; // Show loading state
    } else if (documentErrors.has(docType)) {
      return 'error'; // Show cross
    }
    return 'normal'; // Default state
  };

  const handleDocumentClick = (docType) => {
    // Prevent clicking only if currently uploading (allow re-upload of completed documents)
    if (uploadingDocuments.has(docType)) {
      return;
    }
    
    // Clear any previous error state for this document
    setDocumentErrors(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    
    // Set the selected document to trigger the file picker
    setSelectedDocument(docType);
  };

  // Handle clearing selection - reset all uploading states to prevent stuck spinners
  const handleClearSelection = useCallback(() => {
    resetAllUploadingStates();
    setSelectedDocument('');
  }, [resetAllUploadingStates]);

  const validateStep1 = () => {
    const errors = {};
    const {
      citizenship, id_document_type, id_number, phone_number
    } = step1Data;
    
    if (!citizenship) errors.citizenship = 'Citizenship is required';
    if (!id_document_type) errors.id_document_type = 'ID document type is required';
    if (!id_number) errors.id_number = 'ID number is required';
    if (!phone_number) {
      errors.phone_number = 'Phone number is required';
    } else if (phone_number.length < 8) {
      errors.phone_number = 'Please enter a valid phone number';
    }
    
    return errors;
  };
  
  // Validate Academic Record step (Step3 in wizard flow, but Step2 component)
  const validateAcademicRecord = () => {
    const errors = {};
    
    // Always require institution and campus
    if (!institution) {
      errors.institution = 'Institution is required';
    }
    if (!campus) {
      errors.campus = 'Campus is required';
    }
    
    // Validate based on enrollment status
    if (step1Data.enrollment_status === "currently_registered") {
      if (!step1Data.student_number) {
        errors.student_number = 'Student number is required';
      }
      if (!step1Data.course) {
        errors.course = 'Course is required';
      }
      if (!step1Data.year_of_study) {
        errors.year_of_study = 'Year of study is required';
      }
      if (!step1Data.year_of_registration) {
        errors.year_of_registration = 'Year of registration is required';
      }
      if (!step1Data.study_level) {
        errors.study_level = 'Study level is required';
      }
    } else if (step1Data.enrollment_status === "accepted") {
      if (!step1Data.course) {
        errors.course = 'Course is required';
      }
      if (!step1Data.year_of_registration) {
        errors.year_of_registration = 'Year of registration is required';
      }
      if (!step1Data.study_level) {
        errors.study_level = 'Study level is required';
      }
    }
    // For prospective students, only institution and campus are required (already checked above)
    
    return errors;
  };

  const validateStep4 = () => {
    const errors = {};
    // Validate that the user has agreed to the substance policy
    if (!step4Data.policyAgreed) {
      errors.policyAgreed = 'You must agree to the substance use policy to continue';
    }
    return errors;
  };

  const validateStep5 = () => {
    const errors = {};
    if (!step5Data.sourceOfFunds) {
      errors.sourceOfFunds = 'Source of funds is required';
    }
    return errors;
  };

  const submitStep1Data = async () => {
    setLoadingStates(prev => ({ ...prev, step1: true }));
    try {
      const userId = localStorage.getItem("user_id");
      if (!userId) throw new Error("User ID not found in localStorage.");
      
      // Prepare payload with only Step 1 identification data
      const payload = {
        citizenship: step1Data.citizenship,
        id_document_type: step1Data.id_document_type,
        id_number: step1Data.id_number,
        phone_number: step1Data.phone_number
      };
      
      const res = await axios.post(`/accounts/students/${userId}/profile/`, payload);
      console.log("Step 1 submit response:", res);
      
      if (res.status !== 200 && res.status !== 201) throw new Error();

      return true;
    } catch (e) {
      console.error("Step 1 submit error:", e.response ? e.response.data : e);
      alert('Failed to save identification data.');
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step1: false }));
    }
  };

  const submitStep4Data = async () => {
    const errors = validateStep4();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }
    
    setValidationErrors({});
    
    try {
      setLoadingStates(prev => ({ ...prev, step4: true }));
      // Step 4 data will be submitted with final form submission
      return true;
    } catch (error) {
      console.error('Error in Step 4:', error);
      setValidationErrors({ submit: 'An error occurred. Please try again.' });
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step4: false }));
    }
  };

  const submitStep5Data = async () => {
    setLoadingStates(prev => ({ ...prev, step5: true }));
    try {
      const userId = localStorage.getItem("user_id");
      if (!userId) throw new Error("User ID not found in localStorage.");
      
      // First, create the funding record
      const fundingPayload = {
        user_id: Number(userId),
        source: step5Data.sourceOfFunds
      };
      console.log("Submitting funding payload:", fundingPayload);
      const fundingRes = await axios.post("/accounts/students/funding/create/", fundingPayload);
      if (fundingRes.status !== 200 && fundingRes.status !== 201) {
        throw new Error("Failed to create funding record");
      }
      
      // Second, update the payment method
      const paymentMethodPayload = {
        payment_method: step5Data.sourceOfFunds
      };
      console.log("Updating payment method:", paymentMethodPayload);
      const paymentRes = await axios.post(`/accounts/students/${userId}/update-payment-method/`, paymentMethodPayload);
      if (paymentRes.status !== 200) {
        throw new Error("Failed to update payment method");
      }
      
      // Third, update the registration status to 1
      const regStatusPayload = {
        reg_status: 1
      };
      console.log("Updating registration status:", regStatusPayload);
      const regStatusRes = await axios.post(`/accounts/students/${userId}/update-reg-status/`, regStatusPayload);
      if (regStatusRes.status !== 200) {
        throw new Error("Failed to update registration status");
      }
      
      // Update localStorage and dispatch custom event
      localStorage.setItem('reg_status', '1');
      window.dispatchEvent(new Event('regStatusUpdated'));
      
      // Dispatch wizard completion event to parent
      console.log('Dispatching wizardCompleted event');
      window.dispatchEvent(new Event('wizardCompleted'));
      
      console.log('Setting wizard completed to true');
      setWizardCompleted(true);
      return true;
    } catch (e) {
      console.error("Error submitting Step 5:", e);
      alert('Failed to complete application.');
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step5: false }));
    }
  };

  const handleStep1Change = (field, value) => {
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    setStep1Data(prev => {
      let updated = { ...prev, [field]: value };
      if (field === "enrollment_status") {
        if (value === "currently_registered") {
          // Only clear fields if they're not locked
          if (!lockedFields.student_number) updated.student_number = "";
          if (!lockedFields.course) updated.course = "";
          if (!lockedFields.year_of_study) updated.year_of_study = "";
        } else if (value === "accepted") {
          if (!lockedFields.student_number) updated.student_number = "Pending Registration";
          if (!lockedFields.year_of_study) updated.year_of_study = 1;
          // Keep course for user to fill (don't clear it if locked)
        } else if (value === "prospective") {
          if (!lockedFields.student_number) updated.student_number = "N/A";
          if (!lockedFields.course) updated.course = "N/A";
          if (!lockedFields.year_of_study) updated.year_of_study = "N/A";
        }
      }
      return updated;
    });
  };
  
  const handleStep2Change = (field, value) => {
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    setStep2Data(prev => ({ ...prev, [field]: value }));
  };

  const validateStep2 = () => {
    const errors = {};
    
    if (!step2Data.country) {
      errors.country = 'Country is required';
    }
    
    if (!step2Data.address_line_1) {
      errors.address_line_1 = 'Address Line 1 is required';
    }
    
    if (!step2Data.suburb) {
      errors.suburb = 'Suburb is required';
    }
    
    if (!step2Data.city) {
      errors.city = 'City is required';
    }
    
    if (!step2Data.postcode) {
      errors.postcode = 'Postal code is required';
    }
    
    if (!step2Data.province) {
      errors.province = 'Province/State is required';
    }
    
    return errors;
  };

  const submitStep2Data = async () => {
    const errors = validateStep2();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }
    
    setValidationErrors({});
    
    try {
      setLoadingStates(prev => ({ ...prev, step2: true }));
      // Step 2 data will be submitted with final form submission
      return true;
    } catch (error) {
      console.error('Error in Step 2:', error);
      setValidationErrors({ submit: 'An error occurred. Please try again.' });
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step2: false }));
    }
  };
  
  const handleStep4Change = (field, value) => {
    // Clear validation error for this field when user interacts
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setStep4Data(prev => ({ ...prev, [field]: value }));
  };

  const handleStep5Change = (field, value) => {
    // Clear validation error for this field when user interacts
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setStep5Data(prev => ({ ...prev, [field]: value }));
  };

  // Scroll to top after step transition animation
  useEffect(() => {
    const timeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 500); // Adjust 300ms to match your animation duration
    return () => clearTimeout(timeout);
  }, [currentStep]);

  // Early return for completion state to prevent any other rendering
  if (wizardCompleted) {
    console.log('Early return: Rendering completion success component');
    return (
      <div className="wizardbox">
        <CompletionSuccess />
      </div>
    );
  }

  console.log('Rendering normal wizard, wizardCompleted:', wizardCompleted);

  return (
    <>
      <div className="wizardbox">
        <div className="step-indicator mb-4 d-flex justify-content-between align-items-end" style={{ gap: 0 }}>
          {stepLabels.map((step, index) => {
            const stepNum = index + 1;
            const isActive = currentStep === stepNum;
            const isCompleted = currentStep > stepNum;
            const isStep1Locked = stepNum === 1 && step1Locked;
            const isClickable = stepNum < currentStep && !isStep1Locked;
            
            return (
              <div
              key={stepNum}
              className={`step text-center ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""} ${isStep1Locked ? "locked" : ""}`}
              style={{
              flex: 1,
              position: "relative",
              zIndex: 1,
              minWidth: 100,
              cursor: isClickable ? 'pointer' : 'default',
              opacity: isStep1Locked ? 0.5 : 1
              }}
              onClick={() => {
                if (isClickable) {
                  setCurrentStep(stepNum);
                }
              }}
              title={isStep1Locked ? "Step 1 is locked after submission" : ""}
              >
              <div
              className={`circle d-flex align-items-center justify-content-center mb-2 ${isActive ? "circle-active" : ""} ${isCompleted ? "circle-completed" : ""}`}
              style={{
              width: 60,
              height: 60,
              margin: "0 auto",
              borderRadius: "50%",
              background: isActive
                ? "linear-gradient(135deg, #296f93 60%, #5bc0de 100%)"
                : isCompleted
                ? "linear-gradient(135deg, #28a745 60%, #20c997 100%)"
                : "#e9ecef",
              color: isActive || isCompleted ? "#fff" : "#adb5bd",
              fontSize: 32,
              boxShadow: isActive
                ? "0 0 0 4px #296f9333"
                : isCompleted
                ? "0 0 0 2px #28a74533"
                : "none",
              border: isActive ? "2px solid #296f93" : isCompleted ? "2px solid #28a745" : "2px solid #dee2e6",
              transition: "all 0.2s"
              }}
              >
              <i className={`bi ${isStep1Locked ? 'bi-lock-fill' : step.icon}`} />
              </div>
              <div
              className="label small fw-bold"
              style={{
              color: isActive
                ? "#296f93"
                : isCompleted
                ? "#28a745"
                : "#6c757d",
              fontSize: isActive ? 18 : 15,
              marginTop: 4,
              letterSpacing: 0.5,
              transition: "color 0.2s, font-size 0.2s"
              }}
              >
              {step.label}
              </div>
              {/* Progress bar connector */}
              {index < stepLabels.length - 1 && (
              <div
              style={{
                position: "absolute",
                top: 30,
                right: -50,
                width: 100,
                height: 6,
                background: isCompleted
                ? "linear-gradient(90deg, #28a745 60%, #20c997 100%)"
                : "#dee2e6",
                zIndex: 0,
                borderRadius: 3,
                transition: "background 0.2s"
              }}
              />
              )}
              </div>
            );
          })}
        </div>
        {currentStep === 1 && (
          <Step1 
            step1Data={step1Data}
            handleStep1Change={handleStep1Change}
            institution={institution}
            campus={campus}
            setInstitution={setInstitution}
            setCampus={setCampus}
            validateStep1={validateStep1}
            submitStep1Data={submitStep1Data}
            loadingStates={loadingStates}
            nextStep={() => setCurrentStep(2)}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            showStep1ConfirmModal={showStep1ConfirmModal}
            setShowStep1ConfirmModal={setShowStep1ConfirmModal}
            setStep1Locked={setStep1Locked}
            lockedFields={lockedFields}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            getDocumentCardState={getDocumentCardState}
            handleDocumentClick={handleDocumentClick}
            resetAllUploadingStates={resetAllUploadingStates}
            documentErrors={documentErrors}
            validateDocuments={validateDocuments}
            selectedDocument={selectedDocument}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleClearSelection={handleClearSelection}
            handleUploadStart={handleUploadStart}
          />
        )}
        {currentStep === 2 && (
          <Step2Address
            step2Data={step2Data}
            handleStep2Change={handleStep2Change}
            validateStep2={validateStep2}
            submitStep2Data={submitStep2Data}
            loadingStates={loadingStates}
            previousStep={() => setCurrentStep(1)}
            nextStep={() => setCurrentStep(3)}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            getDocumentCardState={getDocumentCardState}
            handleDocumentClick={handleDocumentClick}
            resetAllUploadingStates={resetAllUploadingStates}
            documentErrors={documentErrors}
            setDocumentErrors={setDocumentErrors}
            selectedDocument={selectedDocument}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleClearSelection={handleClearSelection}
            handleUploadStart={handleUploadStart}
            lockedFields={lockedFields}
          />
        )}
        {currentStep === 3 && (
          <Step3Academic
            key={`step3-${currentStep}`}
            previousStep={() => setCurrentStep(2)}
            nextStep={() => setCurrentStep(4)}
            onMount={() => {}} // No longer needed since we removed the delay logic
            step1Locked={step1Locked}
            // Step 1 data and handlers for Institution and Enrollment
            step1Data={step1Data}
            handleStep1Change={handleStep1Change}
            institution={institution}
            campus={campus}
            setInstitution={setInstitution}
            setCampus={setCampus}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            validateAcademicRecord={validateAcademicRecord}
            lockedFields={lockedFields}
            // Document upload props
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            documentErrors={documentErrors}
            setDocumentErrors={setDocumentErrors}
            getDocumentCardState={getDocumentCardState}
            handleDocumentClick={handleDocumentClick}
            resetAllUploadingStates={resetAllUploadingStates}
            selectedDocument={selectedDocument}
            setSelectedDocument={setSelectedDocument}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleClearSelection={handleClearSelection}
            handleUploadStart={handleUploadStart}
          />
        )}
        {currentStep === 4 && (
          <Step4SubstancePolicy 
            step4Data={step4Data}
            handleStep4Change={handleStep4Change}
            validateStep4={validateStep4}
            submitStep4Data={submitStep4Data}
            loadingStates={loadingStates}
            previousStep={() => setCurrentStep(3)}
            nextStep={() => setCurrentStep(5)}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
          />
        )}
        {currentStep === 5 && (
          <Step5 
            step5Data={step5Data}
            handleStep5Change={handleStep5Change}
            validateStep5={validateStep5}
            submitStep5Data={submitStep5Data}
            loadingStates={loadingStates}
            previousStep={() => setCurrentStep(4)}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
          />
        )}
      </div>
      <style>
        {`
          .lightbox-backdrop {
            animation: fadeIn 0.2s;
          }
          @keyframes fadeIn {
            from { opacity: 0 }
            to { opacity: 1 }
          }
        `}
      </style>
    </>
  );
};

export default StudentFormWizard;